import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  Destroyable,
  DialogService,
  isEmptyTable,
  LoadExpensesByIds,
  localeCompareSort,
  selectSomeOnce,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { DBObject } from '@tv3/constants/dbobjects'
import { NotificationDetailService } from '@tv3/services/notification-detail.service'
import { SecurityDepositsService } from '@tv3/store/security-deposits/security-deposits.service'
import { SecurityDepositOverlayComponent } from '@tv3/containers/security-deposits/overlays/security-deposit-overlay/security-deposit-overlay.component'
import { TableType } from '@tv3/shared/empty-table/table-type'
import * as fromRoot from '@tv3/store/state'
import * as lodash from 'lodash'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { AutomationNotification, AutomationService, AutomationViewGroup } from '@tokeet-frontend/automations'
import { FetchInvoices } from '@tokeet-frontend/invoices'
import { FetchGuests } from '@tv3/store/guest/guest.actions'
import { FetchInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { LoadChargesByIds, selectChargeById } from '@tokeet-frontend/charges'
import { LoadExtrasByIds, selectExtraById } from '@tokeet-frontend/extras'
import { LoadTasksByIds } from '@tokeet-frontend/tasks'
import { LoadIncidentsByIds } from '@tokeet-frontend/incident'
import { LoadInventoriesByIds, selectInventoryById } from '@tokeet-frontend/inventories'
import {
  OpenEditChargeDialog,
  OpenEditExpenseDialog,
  OpenEditExtraDialog,
  OpenEditIncidentDialog,
  OpenEditInventoryDialog,
  OpenEditTaskDialog,
  OpenInventoryOverlay,
} from '@tv3/store/overlay/overlay.actions'
@Component({
  selector: 'app-automation-logs',
  templateUrl: './automation-logs.component.html',
  styleUrls: ['./automation-logs.component.scss'],
})
export class AutomationLogsComponent extends Destroyable implements OnInit {
  @Input() automationGroup: AutomationViewGroup

  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  displayedColumns = ['sent', 'action']

  dataSource = new MatTableDataSource<AutomationNotification>()

  isEmptyTable$ = isEmptyTable(this.dataSource)

  tableType = TableType.AutomationLogs

  constructor(
    private store: Store<fromRoot.State>,
    private automationService: AutomationService,
    private toaster: Toaster,
    private notificationDetailService: NotificationDetailService,
    private securityDepositsService: SecurityDepositsService,
    private dialog: DialogService
  ) {
    super()
  }

  ngOnInit() {
    this.dataSource.data = []
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort

    this.onRefresh()
  }

  onRefresh() {
    this.automationService.logs(this.automationGroup.ids).subscribe((notifications) => {
      this.dataSource.data = notifications
      this.loadObjects(notifications)
    })
  }

  loadObjectsByType(type: DBObject, ids: string[]) {
    switch (type) {
      case DBObject.Invoice:
        return this.store.dispatch(FetchInvoices({ ids }))
      case DBObject.Guest:
        return this.store.dispatch(FetchGuests({ ids }))
      case DBObject.Rental:
        return // loaded
      case DBObject.Inquiry:
        return this.store.dispatch(FetchInquiries({ ids }))
      case DBObject.Charge:
        return this.store.dispatch(LoadChargesByIds({ ids }))
      case DBObject.Extra:
        return this.store.dispatch(LoadExtrasByIds({ ids }))
      case DBObject.Expense:
        return this.store.dispatch(LoadExpensesByIds({ ids }))
      case DBObject.Task:
        return this.store.dispatch(LoadTasksByIds({ ids }))
      case DBObject.Incident:
        return this.store.dispatch(LoadIncidentsByIds({ ids }))
      case DBObject.Inventory:
        return this.store.dispatch(LoadInventoriesByIds({ ids }))
    }
  }

  loadObjects(logs: AutomationNotification[]) {
    logs = lodash.filter(logs, (log) => !!log.object)
    const idsByType = lodash.mapValues(
      lodash.groupBy(logs, (log) => log.object),
      (items) => lodash.uniq(lodash.map(items, (log) => log.pkey))
    )
    lodash.forEach(idsByType, (ids, type: DBObject) => this.loadObjectsByType(type, ids))
  }

  onDetails(log: AutomationNotification) {
    this.openObject(log)
  }

  openObject(log: AutomationNotification) {
    switch (log.object) {
      case DBObject.Inquiry:
        this.notificationDetailService.tryToOpenInquiryDetail(log.pkey)
        break
      case DBObject.Invoice:
        this.notificationDetailService.tryToOpenInvoiceDetail(log.pkey)
        break
      case DBObject.Guest:
        this.notificationDetailService.tryToOpenGuestDetail(log.pkey)
        break
      case DBObject.Rental:
        this.notificationDetailService.tryToOpenRentalDetail(log.pkey)
        break
      case DBObject.Charge:
        this.openCharge(log.pkey)
        break
      case DBObject.Expense:
        this.openExpense(log.pkey)
        break
      case DBObject.Extra:
        this.openExtra(log.pkey)
        break
      case DBObject.Incident:
        this.openIncident(log.pkey)
        break
      case DBObject.Task:
        this.openTask(log.pkey)
        break
      case DBObject.Inventory:
        this.openInventory(log.pkey)
        break
      case DBObject.SecurityDeposit:
        this.openSecurityDeposit(log.pkey)
        break
      default:
        if (log.inquiry_id) {
          this.notificationDetailService.tryToOpenInquiryDetail(log.inquiry_id)
          break
        }
        this.showWarning()
        break
    }
  }

  showWarning() {
    this.toaster.warning($localize`There is no associated entity. Or the entity has been deleted.`)
  }

  openCharge(id: string) {
    this.store.pipe(selectSomeOnce(selectChargeById(id))).subscribe((charge) => {
      this.store.dispatch(OpenEditChargeDialog({ charge }))
    })
  }

  openExtra(id: string) {
    this.store.pipe(selectSomeOnce(selectExtraById(id))).subscribe((extra) => {
      this.store.dispatch(OpenEditExtraDialog({ extra }))
    })
  }

  openExpense(id: string) {
    this.store.dispatch(OpenEditExpenseDialog({ expenseId: id }))
  }

  openIncident(id: string) {
    this.store.dispatch(OpenEditIncidentDialog({ incidentId: id }))
  }

  openTask(id: string) {
    this.store.dispatch(OpenEditTaskDialog({ taskId: id }))
  }

  openInventory(id: string) {
    this.store.pipe(selectSomeOnce(selectInventoryById(id))).subscribe((inventory) => {
      this.store.dispatch(OpenEditInventoryDialog({ inventory }))
    })
  }

  openSecurityDeposit(id: string) {
    this.securityDepositsService.loadDeposits({ pkey: id }).subscribe((response) => {
      const deposit = (response?.data || []).find((d) => d.pkey === id) ?? response?.data?.[0]
      if (deposit) {
        this.dialog.openDrawer(SecurityDepositOverlayComponent, {
          data: { deposit },
          panelClass: 'drawer-full-60',
        })
      } else {
        this.showWarning()
      }
    })
  }
}
